import { VariantWithArticles } from '@digital-bridge/artisan-arya';
import * as Arr from 'fp-ts/lib/Array';
import * as E from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import * as Hooks from 'preact/hooks';
import { FurnitureDescription } from '../../data/furnitureDescription';
import * as Pr from '../../data/product';
import { Product } from '../../data/product';
import * as S from '../../data/summary';
import * as Logging from '../../store/actions/logging';
import * as CatalogueStore from '../../store/catalogue';
import * as Selectors from '../../store/selectors';
import { useGetCatalogueEntries } from './byTenant/useGetCatalogueEntries';
import { useDispatch, useSelector } from './redux';

const mergeLeftArray = E.getApplicativeValidation(Arr.getSemigroup<string>());
// Traverse an array, building up an array of all Left values in the resulting Either
const traverseMergeLeft = Arr.traverse(mergeLeftArray);

export const useLoadCatalogue = () => {
    const dispatch = useDispatch();
    const getProduct = useSelector(Selectors.ProductData.productBySkuFn);
    const catalogueEntries = useGetCatalogueEntries();

    Hooks.useEffect(
        () => {
            if (!getProduct || !catalogueEntries) return;

            const catalogue = pipe(
                catalogueEntries,
                Arr.filterMap(entry =>
                    pipe(
                        entry.furnitureDescription.productIds,
                        traverseMergeLeft((productId: string) =>
                            pipe(productId, getProduct, E.fromNullable([productId])),
                        ),
                        E.fold(
                            missingProducts => {
                                for (let productId of missingProducts) {
                                    dispatch(Logging.logError('missing product', { productId }));
                                }
                                return O.none;
                            },
                            (furnitureProducts: Product[]) =>
                                O.some({
                                    ...entry,
                                    furnitureDescription: {
                                        ...entry.furnitureDescription,
                                        variants: furnitureProducts.map(productToVariant),
                                    } as FurnitureDescription,
                                    price: furnitureProducts.reduce(
                                        (total, variant) => total + variant.price,
                                        0,
                                    ),
                                }),
                        ),
                    ),
                ),
            );
            // seed the catalogue store with catalogue fixtures
            dispatch(CatalogueStore.setCatalogue(catalogue));
        },
        //only run hook when product data changes
        [getProduct, catalogueEntries],
    );
};

export function productToVariant(product: Product): VariantWithArticles {
    const name = Pr.summaryName(product);
    return {
        id: 0,
        canonical_id: Pr.productId(product),
        name,
        description: name,
        thumbnail: Pr.summaryImage(product),
        price: {
            currency: 'GBP',
            value: product.price,
        },
        properties: {
            rattan_colour: product.properties.rattan_colour,
            table_finish: product.properties.table_finish,
        },
        features: [],
        availability: 'in-stock',
        articles: product.articles,
        leadTime: product.leadTime,
    };
}
